import { FC, ReactNode } from 'react'
import Image, { ImageProps } from 'next/image'
import useDetectScroll from 'src/hooks/useDetectScroll'

import Highlight, { HighlightProps } from '../Highlight'
import ActionButton from '../ActionButton'
import { TextLinkProps } from '../A'

import * as SC from './styled'

export type CoverSectionProps = {
  className?: string
  title: string
  titleTag?: string
  breadcrumb?: ReactNode
  backLink?: TextLinkProps
  backgroundColor?: string
  backgroundImageMobile?: ImageProps
  backgroundImageDesktop?: ImageProps
  bottomContent?: ReactNode
  titleImage?: ImageProps
  leftImage?: ImageProps
  rightImage?: ImageProps & { isDoodle?: boolean }
  mobileImage?: ImageProps
  onSettingsClick?: () => void
  highlightProps?: HighlightProps
  showBreadcrumbInMobile?: boolean
}

const CoverSection: FC<CoverSectionProps> = (props) => {
  const {
    className,
    title,
    breadcrumb,
    backLink,
    backgroundColor,
    backgroundImageMobile,
    backgroundImageDesktop,
    bottomContent,
    titleImage,
    leftImage,
    rightImage,
    mobileImage,
    onSettingsClick,
    highlightProps,
    showBreadcrumbInMobile = false,
  } = props

  const { isPageScrolled, scrollDetector } = useDetectScroll()

  return (
    <SC.CoverSection
      className={className}
      backgroundColor={backgroundColor}
      hasBackLink={!!backLink}
    >
      <SC.StickyTarget ref={scrollDetector} />
      <SC.Wrapper hasBottomContent={!!bottomContent}>
        {backLink && (
          <SC.StickyPlaceHolder>
            <SC.BackLinkZone isPageScrolled={isPageScrolled}>
              <ActionButton
                className={isPageScrolled ? 'sticky' : ''}
                actionProps={backLink}
                text={backLink.text}
                icon={backLink?.icon ?? 'backArrow'}
                variantType="link"
                variantColorScheme="black"
              />
            </SC.BackLinkZone>
          </SC.StickyPlaceHolder>
        )}
        {breadcrumb && (
          <SC.BreadcrumbZone $showInMobile={showBreadcrumbInMobile}>
            {breadcrumb}
          </SC.BreadcrumbZone>
        )}
        {titleImage && (
          <SC.TitleImage>
            <Image {...titleImage} />
          </SC.TitleImage>
        )}
        {!titleImage && (
          <SC.Title hasBottomContent={!!bottomContent} hasBackLink={!!backLink}>
            <Highlight
              dangerouslySetInnerHTML
              text={title}
              {...highlightProps}
            />
          </SC.Title>
        )}
        {bottomContent && <SC.BottomContent>{bottomContent}</SC.BottomContent>}
      </SC.Wrapper>
      {(!backgroundImageMobile?.src || !backgroundImageDesktop?.src) && (
        <SC.BackgroundTexures>
          <SC.Dust />
          {leftImage && rightImage && mobileImage && (
            <>
              <SC.LeftImageWrapper>
                <div>
                  <Image objectFit="cover" {...leftImage} />
                </div>
              </SC.LeftImageWrapper>
              <SC.RightImageWrapper $isDoodle={rightImage.isDoodle}>
                <div>
                  <Image objectFit="cover" {...rightImage} />
                </div>
              </SC.RightImageWrapper>
              <SC.MobileImageWrapper>
                <div>
                  <Image objectFit="cover" {...mobileImage} />
                </div>
              </SC.MobileImageWrapper>
            </>
          )}
          {!leftImage && !rightImage && !mobileImage && (
            <>
              <SC.IconsPatternDesktop>
                <SC.IconPattern
                  icon="patternDoodleDesktop"
                  preserveAspectRatio="xMidYMid slice"
                />
              </SC.IconsPatternDesktop>
              <SC.IconsPatternMobile>
                <SC.IconPattern icon="patternDoodleMobile" />
              </SC.IconsPatternMobile>
            </>
          )}
        </SC.BackgroundTexures>
      )}
      {backgroundImageMobile?.src && backgroundImageDesktop?.src && (
        <>
          <SC.BackgroundMobile>
            <Image {...backgroundImageMobile} />
          </SC.BackgroundMobile>
          <SC.BackgroundDesktop>
            <Image {...backgroundImageDesktop} />
          </SC.BackgroundDesktop>
        </>
      )}
      {onSettingsClick && (
        <SC.SettingsButtonWrapper onClick={onSettingsClick}>
          <SC.SettingsButton icon="settings2" />
        </SC.SettingsButtonWrapper>
      )}
    </SC.CoverSection>
  )
}

export default CoverSection
